import { useState, useEffect, useRef, useContext } from 'react';
import type { FC, RefObject } from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  Card,
  CardContent,
  Flex,
  registerIcon,
  defaultThemeProp,
  useI18n,
  Progress,
  usePrevious,
  useLiveLog,
  useAfterInitialEffect
} from '@pega/cosmos-react-core';
import type { RichTextEditorState } from '@pega/cosmos-react-rte';
import * as arrowMicroDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/arrow-micro-down.icon';
import * as caretLeftIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/caret-left.icon';

import type { OpenGraphData } from '../OpenGraphPreview/OpenGraphPreview';

import FeedAttachments, { StyledAttachmentsRegion } from './FeedAttachments';
import FeedContent from './FeedContent';
import FeedReplyInput, { StyledReplyInputContainer } from './FeedReplyInput';
import { FeedContext } from './Feed.context';
import type { CustomEditConfig, DefaultEditConfig, FeedPostProps } from './Feed.types';
import FeedContentHeader from './FeedContentHeader';
import FeedContentFooter from './FeedContentFooter';
import FeedEditRegion from './FeedEditRegion';
import { StyledFeedReplyContainer, StyledReplyContent } from './FeedReply';
import FeedCustomEditRegion from './FeedCustomEditRegion';

registerIcon(arrowMicroDownIcon, caretLeftIcon);

const StyledPostContent = styled.div(({ theme }) => {
  return css`
    margin-inline-start: 1.25rem;
    padding-inline-start: calc(3.5 * ${theme.base.spacing});

    ${StyledAttachmentsRegion} {
      margin-block-start: ${theme.base.spacing};
      margin-inline: 0;
    }
  `;
});

StyledPostContent.defaultProps = defaultThemeProp;

export const StyledReplies = styled.div(
  ({ theme }) => css`
    margin-inline-start: calc(-1.75 * ${theme.base.spacing});

    ${StyledFeedReplyContainer}, ${StyledReplyInputContainer} {
      padding-block-start: ${theme.base.spacing};
    }

    ${StyledReplyInputContainer} {
      ${StyledAttachmentsRegion} {
        margin-block-start: ${theme.base.spacing};
        margin-inline-start: calc(5 * 0.5rem);
        margin-inline-end: calc(5 * 0.5rem);
      }
    }
  `
);

StyledReplies.defaultProps = defaultThemeProp;

export const StyledFeedPost = styled(Card)<{
  $showLines: boolean;
  $customPostType: FeedPostProps['customPostType'];
}>(({ theme, $showLines, $customPostType }) => {
  return css`
    margin-block-start: calc(2 * ${theme.base.spacing});

    ${StyledPostContent},
    ${StyledReplyContent} {
      position: relative;

      ${$showLines &&
      css`
        &::before {
          content: '';
          display: block;
          position: absolute;
          inset-block: 0;
          border-radius: ${theme.base['border-radius']};

          ${$customPostType
            ? css`
                inset-inline-start: -0.0625rem;
                inline-size: 0.125rem;
                background: ${theme.base.palette.warn};
              `
            : css`
                /* Half pixel shift visually centers with avatar. */
                inset-inline-start: calc(-0.0625rem / 2);
                inline-size: 0.0625rem;
                background: ${theme.base.palette['border-line']};
              `}
        }
      `}
    }
  `;
});

StyledFeedPost.defaultProps = defaultThemeProp;

const isDefaultEditConfig = (
  editConfig: DefaultEditConfig | CustomEditConfig | undefined
): editConfig is DefaultEditConfig => {
  return !!editConfig && Object.hasOwn(editConfig, 'defaultValue');
};

const FeedPost: FC<FeedPostProps> = (props: FeedPostProps) => {
  const {
    id,
    avatarSrc,
    fullname,
    username,
    postTimestamp,
    content,
    visibility = 'public',
    postType,
    customPostType,
    onEdit,
    editConfig,
    recipients,
    recipientsCount,
    attachments,
    liked,
    icon,
    postContext,
    userStatus,
    likes,
    likeCount,
    interactionsEnabled,
    maxContentHeight = Infinity,
    onMouseEnter,
    onMouseLeave,
    onCommentClick,
    onLikeClick,
    onLikeCountInteraction,
    likesLoading,
    onLoadMoreLikes,
    onRecipientInteraction,
    recipientsLoading,
    onLoadMoreRecipients,
    onUserClick,
    menuItems,
    edited = false,
    editTimestamp,
    replies = [],
    replyInput,
    replyInputShown,
    replyCount,
    repliesLoading,
    onShowLessReplies,
    onShowMoreReplies,
    ...restProps
  } = props;
  const { autopilotInfo, onLinkRender } = useContext(FeedContext);
  const variant = 'post';

  const t = useI18n();
  const { announcePolite } = useLiveLog();

  const [emptyEditText, setEmptyEditText] = useState(() => {
    if (isDefaultEditConfig(editConfig)) {
      const initialEditText = editConfig.defaultValue ?? content;

      return !initialEditText;
    }

    return false;
  });
  const [replyInputRef, setReplyInputRef] = useState<RefObject<RichTextEditorState> | null>(null);
  const [openGraphData, setOpenGraphData] = useState<OpenGraphData | undefined>(undefined);
  const prevReplies = usePrevious(replies);
  const suppressNextAnnouncement = useRef(false);
  const controllerRef = useRef(new AbortController());

  useEffect(() => {
    if (replyInputShown && replyInputRef) {
      replyInputRef.current?.focus();
    }
  }, [replyInputShown, replyInputRef]);

  useAfterInitialEffect(() => {
    if (prevReplies) {
      const difference = prevReplies.length - replies.length;
      if (difference !== 0 && !suppressNextAnnouncement.current) {
        announcePolite({
          message: difference < 0 ? t('feed_comment_added') : t('feed_comment_removed')
        });
        return;
      }
      suppressNextAnnouncement.current = false;
    }
  }, [replies]);

  const hasReplies = !!replies?.length;
  const adjustedUsername = username === autopilotInfo.label ? autopilotInfo.id : username;

  return (
    <StyledFeedPost
      {...restProps}
      $showLines={hasReplies || !!replyInputShown}
      $customPostType={customPostType}
      onMouseEnter={() => {
        onMouseEnter?.({ id });
      }}
      onMouseLeave={() => {
        onMouseLeave?.({ id });
      }}
    >
      <CardContent>
        <FeedContentHeader
          variant={variant}
          id={id}
          fullname={fullname}
          username={adjustedUsername}
          postTimestamp={postTimestamp}
          avatarSrc={avatarSrc}
          icon={icon}
          userStatus={userStatus}
          visibility={visibility}
          postType={postType}
          customPostType={customPostType}
          postContext={postContext}
          recipients={recipients}
          recipientsCount={recipientsCount}
          menuItems={menuItems}
          edited={edited}
          editTimestamp={editTimestamp}
          recipientsLoading={recipientsLoading}
          onEdit={onEdit}
          onLoadMoreRecipients={onLoadMoreRecipients}
          onRecipientInteraction={onRecipientInteraction}
          onUserClick={onUserClick}
        />
        <StyledPostContent>
          <Flex
            container={{ direction: 'column', pad: [0, editConfig ? 0 : 2, 0, 0] }}
            item={{ grow: 1 }}
          >
            {!editConfig && (
              <FeedContent
                maxContentHeight={maxContentHeight}
                onLinkRender={
                  onLinkRender
                    ? async href => {
                        controllerRef.current.abort();

                        controllerRef.current = new AbortController();

                        try {
                          const newOpenGraphData = await onLinkRender(
                            href,
                            controllerRef.current.signal
                          );
                          if (!controllerRef.current.signal.aborted) {
                            setOpenGraphData(newOpenGraphData);
                          }
                        } catch {
                          // If signal was aborted don't set state.
                        }
                      }
                    : undefined
                }
              >
                {content}
              </FeedContent>
            )}

            {isDefaultEditConfig(editConfig) && typeof content === 'string' && (
              <FeedEditRegion
                defaultValue={editConfig.defaultValue}
                variant={variant}
                id={id}
                content={content}
                visibility={visibility}
                recipients={recipients}
                recipientInput={editConfig.recipientInput}
                onEditSubmit={e => {
                  controllerRef.current.abort();
                  setOpenGraphData(undefined);
                  return editConfig.onEditSubmit(e);
                }}
                onEditCancel={editConfig.onEditCancel}
                onRewriteClick={
                  'onRewriteClick' in editConfig ? editConfig.onRewriteClick : undefined
                }
                progress={editConfig.progress}
                banners={editConfig.banners}
                emptyEditText={emptyEditText}
                setEmptyEditText={setEmptyEditText}
                onUnmount={'onUnmount' in editConfig ? editConfig.onUnmount : undefined}
              />
            )}
            {!!editConfig && !!editConfig.editContent && (
              <FeedCustomEditRegion
                id={id}
                editContent={editConfig.editContent}
                visibility={visibility}
                recipients={recipients}
                recipientInput={editConfig.recipientInput}
                progress={editConfig.progress}
                banners={editConfig.banners}
                onEditSubmit={e => {
                  controllerRef.current.abort();
                  setOpenGraphData(undefined);
                  return editConfig.onEditSubmit(e);
                }}
                onEditCancel={editConfig.onEditCancel}
              />
            )}
            <FeedAttachments
              variant={variant}
              openGraphData={openGraphData}
              attachments={attachments}
            />
          </Flex>
          <FeedContentFooter
            variant={variant}
            id={id}
            username={adjustedUsername}
            liked={liked}
            likes={likes}
            likeCount={likeCount}
            interactionsEnabled={interactionsEnabled}
            likesLoading={likesLoading}
            replies={replies}
            replyCount={replyCount}
            repliesLoading={repliesLoading}
            onCommentClick={onCommentClick}
            onLikeClick={onLikeClick}
            onLikeCountInteraction={onLikeCountInteraction}
            onLoadMoreLikes={onLoadMoreLikes}
            onShowMoreReplies={onShowMoreReplies}
            onShowLessReplies={onShowLessReplies}
          />

          <StyledReplies>
            {hasReplies && replies}
            {replyInputShown && (
              <FeedReplyInput
                defaultValue={replyInput.defaultValue}
                onSetInputRef={({ ref }) => {
                  setReplyInputRef(ref);
                }}
                id={id}
                attachments={replyInput.attachments}
                onFilesAdded={replyInput.onFilesAdded}
                onRewriteClick={replyInput.onRewriteClick}
                progress={replyInput.progress}
                banners={replyInput.banners}
                onSubmit={replyInput.onSubmit}
                onUnmount={'onUnmount' in replyInput ? replyInput.onUnmount : undefined}
              />
            )}
          </StyledReplies>
          {onShowLessReplies && !!replies.length && (
            <Flex container={{ justify: 'center', pad: [0, 0, 1], gap: 1 }}>
              <Button
                variant='link'
                onClick={() => {
                  onShowLessReplies({ id });
                  suppressNextAnnouncement.current = true;
                }}
              >
                {t('show_less')}
              </Button>
              {repliesLoading && <Progress placement='inline' />}
            </Flex>
          )}
        </StyledPostContent>
      </CardContent>
    </StyledFeedPost>
  );
};

export default FeedPost;
